import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { MessageSquareQuote, Plus, Edit, Trash2, StarIcon, EyeOff, Eye, X, ArrowUp, ArrowDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '', company: '', comment: '', rating: 5, avatar_url: '', is_active: true, order: 0,
};

const UltraTestimonials = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, editing: null, form: EMPTY });
  const token = localStorage.getItem('ultra_admin_token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ultra-admin/testimonials`, authHeaders);
      setItems(res.data.testimonials || []);
    } catch (e) {
      toast.error('Referanslar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => setModal({ open: true, editing: null, form: { ...EMPTY, order: items.length } });
  const openEdit = (t) => setModal({ open: true, editing: t, form: { ...t } });
  const close = () => setModal({ open: false, editing: null, form: EMPTY });

  const save = async () => {
    const { form, editing } = modal;
    if (!form.name.trim() || !form.comment.trim()) {
      toast.error('Ad ve yorum zorunludur');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/ultra-admin/testimonials/${editing.id}`, form, authHeaders);
        toast.success('Referans güncellendi');
      } else {
        await axios.post(`${API}/ultra-admin/testimonials`, form, authHeaders);
        toast.success('Referans eklendi');
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaydedilemedi');
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`"${t.name}" referansını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/ultra-admin/testimonials/${t.id}`, authHeaders);
      toast.success('Silindi');
      load();
    } catch (e) {
      toast.error('Silinemedi');
    }
  };

  const toggleActive = async (t) => {
    try {
      await axios.put(`${API}/ultra-admin/testimonials/${t.id}`, { ...t, is_active: !t.is_active }, authHeaders);
      load();
    } catch (e) {
      toast.error('Güncellenemedi');
    }
  };

  const changeOrder = async (t, delta) => {
    try {
      await axios.put(`${API}/ultra-admin/testimonials/${t.id}`, { ...t, order: (t.order || 0) + delta }, authHeaders);
      load();
    } catch (e) {
      toast.error('Sıra güncellenemedi');
    }
  };

  const f = modal.form;
  const setF = (patch) => setModal(m => ({ ...m, form: { ...m.form, ...patch } }));

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="ultra-testimonials-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquareQuote className="w-6 h-6 text-purple-600" />
            Referanslar (Testimonials)
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Ana sayfa &quot;Müşterilerimiz Ne Diyor?&quot; bölümünde gösterilen yorumlar. Buradan eklediklerin anında yayınlanır.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-testimonial-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Yeni Referans
        </Button>
      </div>

      {loading ? (
        <div className="p-8 text-gray-500">Yükleniyor…</div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <MessageSquareQuote className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-1">Henüz referans eklenmedi.</p>
          <p className="text-sm text-gray-500 mb-4">Landing sayfası şu anda hardcoded 3 örnek referans gösteriyor. Buradan ekleyince gerçek verinle değişir.</p>
          <Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700 text-white">
            <Plus className="w-4 h-4 mr-1.5" /> İlk Referansı Ekle
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((t, idx) => (
            <Card key={t.id} className={`p-4 ${!t.is_active ? 'opacity-60' : ''}`} data-testid={`testimonial-row-${t.id}`}>
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center pt-1">
                  <button
                    onClick={() => changeOrder(t, -1)}
                    className="text-gray-400 hover:text-gray-700 p-0.5"
                    disabled={idx === 0}
                    title="Yukarı taşı"
                  ><ArrowUp className="w-3.5 h-3.5" /></button>
                  <span className="text-xs text-gray-500 font-mono">{t.order}</span>
                  <button
                    onClick={() => changeOrder(t, 1)}
                    className="text-gray-400 hover:text-gray-700 p-0.5"
                    disabled={idx === items.length - 1}
                    title="Aşağı taşı"
                  ><ArrowDown className="w-3.5 h-3.5" /></button>
                </div>
                {t.avatar_url ? (
                  <img src={t.avatar_url} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-semibold">
                    {(t.name || '?').slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{t.name}</span>
                    {t.company && <span className="text-sm text-gray-500">· {t.company}</span>}
                    <div className="flex">
                      {[...Array(t.rating)].map((_, i) => <StarIcon key={i} className="w-3.5 h-3.5 text-yellow-400 fill-current" />)}
                    </div>
                    {!t.is_active && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700 uppercase">Pasif</span>}
                  </div>
                  <blockquote className="text-sm text-gray-700 italic mt-1">&ldquo;{t.comment}&rdquo;</blockquote>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => toggleActive(t)} title={t.is_active ? 'Pasif yap' : 'Aktif yap'} data-testid={`toggle-testimonial-${t.id}`}>
                    {t.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-testimonial-${t.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(t)} className="text-rose-600" data-testid={`delete-testimonial-${t.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {modal.open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">{modal.editing ? 'Referansı Düzenle' : 'Yeni Referans'}</h3>
              <button onClick={close} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label>Ad Soyad *</Label>
                <Input value={f.name} onChange={e => setF({ name: e.target.value })} placeholder="Ahmet Kaya" data-testid="testimonial-name" />
              </div>
              <div>
                <Label>Şirket</Label>
                <Input value={f.company} onChange={e => setF({ company: e.target.value })} placeholder="TechnoSoft A.Ş." data-testid="testimonial-company" />
              </div>
              <div>
                <Label>Yorum *</Label>
                <Textarea value={f.comment} onChange={e => setF({ comment: e.target.value })} rows={3} placeholder="Advancecash.io sayesinde..." data-testid="testimonial-comment" />
              </div>
              <div>
                <Label>Avatar URL (opsiyonel)</Label>
                <Input value={f.avatar_url} onChange={e => setF({ avatar_url: e.target.value })} placeholder="https://..." data-testid="testimonial-avatar" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Yıldız (1-5)</Label>
                  <Input type="number" min={1} max={5} value={f.rating} onChange={e => setF({ rating: Number(e.target.value) })} data-testid="testimonial-rating" />
                </div>
                <div>
                  <Label>Sıra</Label>
                  <Input type="number" value={f.order} onChange={e => setF({ order: Number(e.target.value) })} data-testid="testimonial-order" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={f.is_active} onChange={e => setF({ is_active: e.target.checked })} className="w-4 h-4 rounded" data-testid="testimonial-active" />
                <span className="text-sm">Aktif (ana sayfada görünsün)</span>
              </label>
            </div>
            <div className="p-5 border-t bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={close}>Vazgeç</Button>
              <Button onClick={save} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="testimonial-save">
                {modal.editing ? 'Güncelle' : 'Ekle'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltraTestimonials;
